/**
 * Pure, React-free helpers for the Affiliates Reports dashboard: period-range math,
 * previous-period (delta) math, month keys, metric series extraction and CSV
 * serialization. Kept free of React/axios/tokens so they unit-test in isolation
 * (coverage is deferred to the consolidation pass; the functions are written pure
 * so it is a drop-in later).
 *
 * All dates are calendar `YYYY-MM-DD` strings built from LOCAL date parts (never
 * `toISOString`, which is UTC and would drift a day near midnight). The affiliate
 * API treats `from`/`to` as inclusive calendar days.
 */
import type { AffiliateMetrics, AffiliatePerformanceRow } from './types';

export type PeriodPreset = 'month' | '7d' | '30d' | '90d' | 'custom';
export type Dimension = 'affiliate' | 'campaign' | 'date';
/** `metric` = one selected metric as a single line; `dimension` = that metric as one line per top-N entity. Both stay single-axis. */
export type ChartMode = 'metric' | 'dimension';

export interface DateRange {
  from: string;
  to: string;
}

/** The performance metrics the dashboard plots / tiles (all present in the wire `metrics`). */
export type PerfMetricKey = 'registrations' | 'ftds' | 'deposits_usd' | 'ggr_usd' | 'ngr_usd' | 'clicks';

/** Trend-chart metric options, in the order they appear in the picker. */
export const TREND_METRIC_KEYS: PerfMetricKey[] = [
  'ngr_usd',
  'ggr_usd',
  'deposits_usd',
  'registrations',
  'ftds',
  'clicks',
];

export const METRIC_KIND: Record<PerfMetricKey, 'count' | 'money'> = {
  registrations: 'count',
  ftds: 'count',
  clicks: 'count',
  deposits_usd: 'money',
  ggr_usd: 'money',
  ngr_usd: 'money',
};

// --- date primitives (local, DST-safe via the parts constructor) ------------

const pad = (n: number): string => String(n).padStart(2, '0');

/** `YYYY-MM-DD` from a Date's LOCAL parts. */
export const isoDay = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Parse a `YYYY-MM-DD` back to a LOCAL midnight Date (no timezone shift). */
export const parseDay = (s: string): Date => {
  const [y, m, d] = s.split('-').map((p) => Number(p));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

/** A new Date shifted by whole days, staying at local midnight. */
const addDays = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Inclusive day count of a range (Sep 1 → Sep 15 = 15). */
export const dayCount = (range: DateRange): number =>
  Math.round((parseDay(range.to).getTime() - parseDay(range.from).getTime()) / 86_400_000) + 1;

// --- period ranges ----------------------------------------------------------

/** The `{from,to}` for a preset relative to `today` (custom is held by the caller, not derived here). */
export const rangeFromPreset = (preset: Exclude<PeriodPreset, 'custom'>, today: Date): DateRange => {
  const to = isoDay(today);
  if (preset === 'month') return { from: isoDay(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  const span = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  return { from: isoDay(addDays(today, -(span - 1))), to };
};

/**
 * The comparison range for deltas. For `month` it is prior-month-to-date (same
 * number of elapsed days from the 1st of the previous month, clamped to that
 * month's length); for every other preset/custom range it is the equal-length
 * window immediately preceding `from`.
 */
export const previousRange = (range: DateRange, preset: PeriodPreset, today: Date): DateRange => {
  const len = dayCount(range);
  if (preset === 'month') {
    const prevFirst = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastOfPrev = new Date(today.getFullYear(), today.getMonth(), 0);
    let prevTo = addDays(prevFirst, len - 1);
    if (prevTo.getTime() > lastOfPrev.getTime()) prevTo = lastOfPrev;
    return { from: isoDay(prevFirst), to: isoDay(prevTo) };
  }
  const from = parseDay(range.from);
  const prevTo = addDays(from, -1);
  const prevFrom = addDays(prevTo, -(len - 1));
  return { from: isoDay(prevFrom), to: isoDay(prevTo) };
};

/** `YYYY-MM` of a `YYYY-MM-DD` (or Date). */
export const monthKey = (day: string): string => day.slice(0, 7);

/** The `YYYY-MM` before a given `YYYY-MM`. */
export const previousMonthKey = (month: string): string => {
  const [y, m] = month.split('-').map((p) => Number(p));
  const d = new Date(y, (m ?? 1) - 1 - 1, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

// --- deltas -----------------------------------------------------------------

export interface Delta {
  /** Absolute percentage magnitude (the sign is carried by `dir`). */
  pct: number;
  dir: 'increase' | 'decrease';
}

/**
 * Period-over-period delta. Returns `null` when there is no valid basis (previous
 * value missing or zero) so the caller renders the `–` glyph rather than `↑∞%`.
 */
export const deltaPct = (curr: number | undefined | null, prev: number | undefined | null): Delta | null => {
  if (typeof curr !== 'number' || !Number.isFinite(curr)) return null;
  if (typeof prev !== 'number' || !Number.isFinite(prev) || prev === 0) return null;
  const change = ((curr - prev) / prev) * 100;
  return { pct: Math.abs(change), dir: curr >= prev ? 'increase' : 'decrease' };
};

// --- series / rows ----------------------------------------------------------

const num = (n: number | undefined | null): number => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

/** A metric's value from a metric block (0 when absent, for plotting). */
export const metricValue = (m: AffiliateMetrics | undefined, key: PerfMetricKey): number => num(m?.[key]);

/** Daily point series for one metric from `group_by=date` rows (missing days → 0). */
export const dailySeries = (rows: AffiliatePerformanceRow[], key: PerfMetricKey): number[] =>
  rows.map((r) => metricValue(r.metrics, key));

/** The `YYYY-MM-DD` label per daily row (for the chart tooltip). */
export const dailyPointLabels = (rows: AffiliatePerformanceRow[]): string[] => rows.map((r) => r.date ?? '');

/** Up to `max` evenly spaced numeric `M/D` axis labels for a daily series. */
export const axisLabels = (rows: AffiliatePerformanceRow[], max = 6): string[] => {
  const days = rows.map((r) => r.date).filter((d): d is string => !!d);
  if (days.length <= max) return days.map(shortDay);
  const step = (days.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => shortDay(days[Math.round(i * step)]));
};

/** `2026-09-03` → `9/3` (locale-neutral, no month-name i18n needed). */
export const shortDay = (day: string): string => {
  const d = parseDay(day);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/** Identity of a breakdown row for the active dimension. */
export const rowEntityId = (row: AffiliatePerformanceRow, dimension: Dimension): string | undefined =>
  dimension === 'campaign' ? row.campaign : row.affiliate?.id;

/** Display name of a breakdown row (falls back to the id, then the provided label). */
export const rowEntityName = (
  row: AffiliatePerformanceRow,
  dimension: Dimension,
  unknownLabel: string,
): string =>
  dimension === 'campaign'
    ? row.campaign ?? unknownLabel
    : row.affiliate?.display_name?.trim() || row.affiliate?.id || unknownLabel;

export interface TopEntity {
  id: string;
  name: string;
}

/** Top-N breakdown entities by a metric, descending, for the multi-line trend. */
export const topEntities = (
  rows: AffiliatePerformanceRow[],
  dimension: Dimension,
  key: PerfMetricKey,
  n: number,
  unknownLabel: string,
): TopEntity[] =>
  rows
    .map((row) => ({ id: rowEntityId(row, dimension), name: rowEntityName(row, dimension, unknownLabel), v: metricValue(row.metrics, key) }))
    .filter((e): e is { id: string; name: string; v: number } => !!e.id)
    .sort((a, b) => b.v - a.v)
    .slice(0, n)
    .map(({ id, name }) => ({ id, name }));

// --- formatting -------------------------------------------------------------

/** `1,234` integer count. */
export const formatCountValue = (n: number): string => Math.round(n).toLocaleString('en-US');

/** Currency-aware integer money (`$1,234`), from the report's own `currency`; falls back to `$` on a bad code. */
export const formatMoneyValue = (n: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `$${Math.round(n).toLocaleString('en-US')}`;
  }
};

/** Display a metric value by its kind (count vs money). */
export const displayMetric = (value: number, key: PerfMetricKey, currency: string): string =>
  METRIC_KIND[key] === 'money' ? formatMoneyValue(value, currency) : formatCountValue(value);

/** `233%` (integer, unsigned; the direction is shown by the badge arrow/colour). */
export const formatDeltaPct = (pct: number): string => `${Math.round(pct)}%`;

// --- CSV --------------------------------------------------------------------

const csvCell = (value: string | number): string => {
  // Numeric cells never need formula-escaping (and must not be, or a negative like -5 would
  // be corrupted into text).
  if (typeof value === 'number') return String(value);
  // Neutralize spreadsheet formula injection: a cell that starts with = + - @ (or tab/CR) is
  // evaluated by Excel/Sheets/LibreOffice. Affiliate/campaign names are attacker-influenced
  // (a partner sets their own display name), so prefix a leading trigger with a single quote
  // so the value renders as inert text.
  let s = value;
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Serialize a header row + data rows to CSV text (RFC-4180 quoting). */
export const toCsv = (headers: string[], rows: Array<Array<string | number>>): string =>
  [headers, ...rows].map((cols) => cols.map(csvCell).join(',')).join('\r\n');
